import React from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from '@mui/material';
import WebDashboardLayoutModeField from '../../Components/WebDashboardLayoutModeField';
import templateTranslations from '../../languages/template.json';
import { DeleteIcon } from '../../svg/UtilsIcons';

const TemplateEditTabRolePopup = ({
    t,
    lang,
    editingTabRoleData,
    editPopupAnchor,
    editPopupRef,
    details,
    currentDashboard,
    allRoles,
    webDashboardT,
    alert,
    setDetails,
    setEditingTabRoleData,
    setEditPopupAnchor,
    setCurrentDashboard,
    setSearchParams,
    handleEditTabNameChange,
    handleEditTabRoleSelection,
    saveEditedTabRoles,
}) => {
    if (typeof document === 'undefined' || !editingTabRoleData || !editPopupAnchor) {
        return null;
    }

    return createPortal(
        <div
            ref={editPopupRef}
            className="role-editor-popup role-editor-popup--portal"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            style={{
                position: 'fixed',
                top: editPopupAnchor.top,
                left: editPopupAnchor.left,
                transform: 'translateX(-50%)',
                zIndex: 100000,
                backgroundColor: 'var(--cell-background)',
                border: '1px solid var(--modal-border)',
                borderRadius: '8px',
                padding: '10px',
                boxShadow: '0 12px 30px rgba(0,0,0,0.35)',
                width: '320px',
                maxWidth: 'calc(100vw - 24px)',
                maxHeight: 'calc(100vh - 24px)',
                overflowY: 'auto',
                overflowX: 'hidden',
            }}
        >
            <h4
                style={{
                    marginBottom: '10px',
                    fontSize:
                        'calc(var(--ui-font-size) * var(--font-size-multiplier))',
                }}
            >
                {t.editRolesTitle}
            </h4>

            <div style={{ marginBottom: 10 }}>
                <div
                    style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: '#AEEAFF',
                        marginBottom: 8,
                    }}
                >
                    {t.tabName || 'Tab name'}

                    {details.webdashboard?.length > 0 ? (
                        <Tooltip title={t.dashboardDeleteCurrentTab}>
                            <button
                                type="button"
                                className="details-delete-btn"
                                style={{ position: 'absolute', flexShrink: 0, marginRight: '8px', right: '2%' }}
                                onClick={async (e) => {
                                    e.stopPropagation();
                                    const targetIndex = editingTabRoleData?.index;
                                    if (targetIndex == null || targetIndex < 0) return;

                                    const confirmed = await alert({
                                        type: 'confirm',
                                        variant: 'danger',
                                        icon: 'delete',
                                        title: t.confirmTitle || 'Confirm',
                                        message: t.dashboardDeleteTabWarning,
                                        confirmText: t.delete || templateTranslations[lang].delete || 'Delete',
                                        cancelText: t.cancel || templateTranslations[lang].buttonCancel || 'Cancel',
                                        showCancel: true,
                                    });
                                    if (!confirmed) return;

                                    const totalTabs = details.webdashboard?.length || 0;
                                    let nextIndex = currentDashboard;
                                    if (totalTabs <= 1) {
                                        nextIndex = 0;
                                    } else if (targetIndex < currentDashboard) {
                                        nextIndex = Math.max(0, currentDashboard - 1);
                                    } else if (targetIndex === currentDashboard) {
                                        nextIndex = currentDashboard > 0 ? currentDashboard - 1 : 0;
                                    }

                                    setDetails((prevDetails) => {
                                        const newWebdashboards = (prevDetails.webdashboard || []).filter(
                                            (_, index) => index !== targetIndex
                                        );
                                        return {
                                            ...prevDetails,
                                            webdashboard: newWebdashboards,
                                        };
                                    });

                                    setEditingTabRoleData(null);
                                    setEditPopupAnchor(null);

                                    setCurrentDashboard(nextIndex);
                                    setSearchParams((prev) => {
                                        prev.set('dashboardIdx', nextIndex.toString());
                                        return prev;
                                    }, { replace: true });
                                }}
                            >
                                <DeleteIcon color={'var(--root-background)'} />
                            </button>
                        </Tooltip>
                    ) : <span />}
                </div>
                <input
                    type="text"
                    className="add-column"
                    maxLength={50}
                    value={editingTabRoleData.name || ''}
                    onChange={(e) =>
                        handleEditTabNameChange(e.target.value)
                    }
                    style={{
                        paddingInlineStart: '10px',
                        color: 'var(--cell-color)',
                        caretColor: 'var(--cell-color)',
                        width: '100%',
                    }}
                />
            </div>

            <div style={{ marginBottom: 10 }}>
                <WebDashboardLayoutModeField
                    value={editingTabRoleData.layoutMode}
                    title={webDashboardT.layoutModeLabel || 'Layout Mode'}
                    helperText={
                        webDashboardT.layoutModeReadonlyHelper ||
                        'This layout mode is locked after the tab is created.'
                    }
                    translations={webDashboardT}
                    readOnly
                    compact
                />
            </div>

            <div
                className="multi-select-container"
                style={{ maxHeight: '160px', overflowY: 'auto' }}
            >
                {allRoles
                    .filter((role) => !['super', 'owner'].includes(role))
                    .map((role) => (
                        <div
                            key={role}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEditTabRoleSelection(role);
                            }}
                            style={{
                                padding: '4px 0',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                fontSize:
                                    'calc(var(--body-font-size) * var(--font-size-multiplier))',
                            }}
                        >
                            <input
                                type="checkbox"
                                readOnly
                                checked={editingTabRoleData.roles.includes(role)}
                                style={{
                                    marginRight: '8px',
                                    cursor: 'pointer',
                                }}
                            />
                            {role}
                        </div>
                    ))}
            </div>

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '10px',
                }}
            >
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        className="btn btn-cancel"
                        style={{
                            fontSize:
                                'calc(var(--body-font-size) * var(--font-size-multiplier))',
                            padding: '6px 12px',
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setEditingTabRoleData(null);
                            setEditPopupAnchor(null);
                        }}
                    >
                        {t.cancel}
                    </button>

                    <button
                        className="btn edit-btn-save-role"
                        style={{
                            fontSize:
                                'calc(var(--body-font-size) * var(--font-size-multiplier))',
                            padding: '6px 12px',
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            saveEditedTabRoles();
                        }}
                    >
                        {t.apply}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default TemplateEditTabRolePopup;
